import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Button, CircularProgress,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { useCreateUser } from '../../hooks/useUsers';
import { useNotification } from '../../context/NotificationContext';
import PortalPicker from '../PortalPicker';

const SectionHeader = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
        <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
        >
            {title}
        </Typography>
    </Box>
);

export default function AdminUserCreate() {
    const navigate = useNavigate();
    const createUser = useCreateUser();
    const { showError, showWarning, showSuccess } = useNotification();

    const usernameRef = useRef(null);
    useEffect(() => { usernameRef.current?.focus(); }, []);
    useEffect(() => { document.title = 'Novi korisnik | Media Scanner'; }, []);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [alertLimit, setAlertLimit] = useState(3);
    const [ssLimit, setSsLimit] = useState(0);
    const [briefingLimit, setBriefingLimit] = useState(0);
    const [smartAlertLimit, setSmartAlertLimit] = useState(0);
    const [locale, setLocale] = useState('hr');
    const [selectedPortals, setSelectedPortals] = useState([]);
    const [saving, setSaving] = useState(false);

    const isFormValid = username.trim() && password.trim();

    const handleSave = async () => {
        if (!username.trim()) { showWarning('Korisničko ime je obavezno polje'); return; }
        if (!password.trim()) { showWarning('Lozinka je obavezno polje'); return; }

        setSaving(true);
        createUser.mutate(
            {
                username: username.trim(),
                password: password,
                alert_limit: alertLimit,
                scheduled_searches_limit: ssLimit,
                briefing_limit: briefingLimit,
                smart_alert_limit: smartAlertLimit,
                locale: locale,
                allowed_portal_ids: selectedPortals.map(p => p.id),
            },
            {
                onSuccess: () => {
                    showSuccess('Korisnik uspješno kreiran');
                    navigate('/admin/users');
                },
                onError: (err) => {
                    setSaving(false);
                    if (err.response?.status === 409) {
                        showError('Korisničko ime već postoji');
                    } else {
                        showError('Greška pri kreiranju korisnika. Pokušajte ponovo.');
                    }
                },
            }
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                    Novi korisnik
                </Typography>

                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <SectionHeader title="Pristupni podaci" />
                    <TextField
                        inputRef={usernameRef}
                        label="Korisničko ime"
                        fullWidth
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        sx={{ mb: 2 }}
                        size="small"
                    />
                    <TextField
                        label="Lozinka"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        size="small"
                    />

                    <SectionHeader title="Limiti" />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Alert limit"
                            type="number"
                            value={alertLimit}
                            onChange={e => setAlertLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Auto. pretrage limit"
                            type="number"
                            value={ssLimit}
                            onChange={e => setSsLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Briefing limit"
                            type="number"
                            value={briefingLimit}
                            onChange={e => setBriefingLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Smart alert limit"
                            type="number"
                            value={smartAlertLimit}
                            onChange={e => setSmartAlertLimit(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 150 }}
                            inputProps={{ min: 0 }}
                        />
                    </Box>

                    <SectionHeader title="Jezik" />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Jezik</InputLabel>
                        <Select value={locale} label="Jezik" onChange={e => setLocale(e.target.value)}>
                            <MenuItem value="hr">Hrvatski</MenuItem>
                            <MenuItem value="si">Slovenski</MenuItem>
                            <MenuItem value="ro">Rumunjski</MenuItem>
                            <MenuItem value="ba">Bosanski</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                        </Select>
                    </FormControl>

                    <SectionHeader title="Dozvoljeni portali" />
                    <PortalPicker value={selectedPortals} onChange={setSelectedPortals} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/admin/users')}
                            sx={{ textTransform: 'none', borderRadius: '20px' }}
                        >
                            Odustani
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!isFormValid || saving}
                            sx={{ textTransform: 'none', borderRadius: '20px', minWidth: 120 }}
                        >
                            {saving ? <CircularProgress size={20} /> : 'Spremi'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
